/**
 * Image utilities.
 * Uses Supabase Storage Render API for on-the-fly WebP conversion and resizing.
 * Uses wsrv.nl proxy for local /images/ assets on production.
 * Non-Supabase, non-local URLs pass through unchanged.
 */

import { sha256 } from "./sha256";

// Bundled bucket tiles — served from Vercel CDN (fast cold edge) instead
// of Supabase storage (slow cold edge). Vite hashes + fingerprints these.
import BUCKET_SALES_400    from "@/assets/buckets/bucket-sales-400.webp";
import BUCKET_SERVICE_400  from "@/assets/buckets/bucket-service-400.webp";
import BUCKET_PARTS_400    from "@/assets/buckets/bucket-parts-400.webp";
import BUCKET_RENTALS_400  from "@/assets/buckets/bucket-rentals-400.webp";

/* ── Pre-built variant manifest (populated once at app boot) ── */
type ManifestEntry = { thumb: string; card: string; detail: string; hero: string };
/** Raw entry as emitted by the edge function — short keys to shrink payload. */
type RawManifestEntry = Partial<ManifestEntry> & { t?: string; c?: string; d?: string; h?: string };
let MANIFEST: Record<string, ManifestEntry> = {};

/* ── Manifest variant delivery host ──
 * Prebuilt variants live in the Supabase `splash-assets` bucket AND are
 * mirrored to the myrvpart.com CDN by `sync-splash-to-cdn`. Serve them from
 * the CDN so the LCP path avoids the third-party Supabase DNS+TLS handshake
 * and gets Cloudflare edge caching. If a variant hasn't reached the mirror
 * yet, `manifestToSupabase()` gives ResilientImg an origin fallback.
 *
 * FLIPPED OFF (2026-08-11): /rv-search Lighthouse flagged every card.webp on
 * www.myrvpart.com for third-party cookies (fpestid, __ss_tk) plus a batch of
 * mirror 404s for freshly built variants. The Supabase storage origin is
 * cookie-free, already preconnected in __root.tsx, and is the source of truth
 * so no variant can 404 because the mirror lagged. Card/thumb images are not
 * on the LCP path, so the tradeoff is a win. */
const MANIFEST_USE_CDN = false;
const MANIFEST_CDN_BASE = "https://www.myrvpart.com/images/splash-assets";
const MANIFEST_SB_BASE =
  "https://cpkvmigljcrjxctoixoq.supabase.co/storage/v1/object/public/splash-assets";

/** Rewrite a Supabase-hosted prebuilt manifest variant URL onto the CDN mirror. */
export function toCdnManifest(url: string): string {
  if (!url) return url;
  if (!MANIFEST_USE_CDN) return manifestToSupabase(url);
  if (!url.includes("/splash-assets/manifest/")) return url;
  if (url.startsWith(MANIFEST_CDN_BASE)) return url;
  const idx = url.indexOf("/splash-assets/manifest/");
  return MANIFEST_CDN_BASE + url.slice(idx + "/splash-assets".length);
}

/** Inverse of `toCdnManifest` — used as an origin fallback when the mirror 404s. */
export function manifestToSupabase(url: string): string {
  if (!url || !url.startsWith(MANIFEST_CDN_BASE)) return url;
  if (!url.includes("/manifest/")) return url;
  return MANIFEST_SB_BASE + url.slice(MANIFEST_CDN_BASE.length);
}

/** Normalize one raw entry (short or long keys) into the canonical long-key shape. */
function normalizeEntry(e: RawManifestEntry): ManifestEntry {
  const thumb  = e.thumb  ?? e.t ?? "";
  const card   = e.card   ?? e.c ?? "";
  const detail = e.detail ?? e.d ?? "";
  const hero   = e.hero   ?? e.h ?? "";
  const fb = detail || hero || card || thumb;
  return {
    thumb:  toCdnManifest(thumb  || fb),
    card:   toCdnManifest(card   || fb),
    detail: toCdnManifest(detail || fb),
    hero:   toCdnManifest(hero   || fb),
  };
}

function normalizeMap(raw: Record<string, RawManifestEntry> | undefined | null): Record<string, ManifestEntry> {
  const out: Record<string, ManifestEntry> = {};
  if (!raw) return out;
  for (const k in raw) { const v = raw[k]; if (v) out[k] = normalizeEntry(v); }
  return out;
}

/** Hydrate the manifest map. Called once from <App> after fetching /image-manifest. */
export function setImageManifest(map: Record<string, RawManifestEntry> | Record<string, ManifestEntry>) {
  MANIFEST = normalizeMap(map as Record<string, RawManifestEntry>);
  manifestHydrated = true;
  const resolve = resolveManifestReady;
  if (resolve) {
    resolve();
    resolveManifestReady = null;
  }
}

/* ── Manifest readiness signaling ──
 * `whenManifestReady()` resolves as soon as the manifest is populated, OR after
 * an optional timeout (default 1500ms). Used by main.tsx to delay the first
 * React render until the manifest is in memory — eliminating the race where
 * components paint with on-the-fly /render/image/ URLs before the async
 * fetch returns.
 */
let manifestHydrated = false;
let resolveManifestReady: (() => void) | null = null;
const manifestReadyPromise: Promise<void> = new Promise((resolve) => {
  resolveManifestReady = resolve;
});

/** Synchronous bootstrap: pull from window.__IMAGE_MANIFEST__ if the
 *  index.html early-loader populated it before the JS bundle parsed. */
if (typeof window !== "undefined") {
  try {
    const w = window as unknown as { __IMAGE_MANIFEST__?: Record<string, RawManifestEntry> };
    if (w.__IMAGE_MANIFEST__ && typeof w.__IMAGE_MANIFEST__ === "object") {
      MANIFEST = normalizeMap(w.__IMAGE_MANIFEST__);
      manifestHydrated = true;
      const resolve = resolveManifestReady as (() => void) | null;
      if (resolve) { resolve(); resolveManifestReady = null; }
    }
    // Listen for the index.html bootstrap fetch completing post-module-load.
    window.addEventListener("image-manifest-ready", () => {
      const ww = window as unknown as { __IMAGE_MANIFEST__?: Record<string, RawManifestEntry> };
      if (ww.__IMAGE_MANIFEST__) {
        MANIFEST = normalizeMap(ww.__IMAGE_MANIFEST__);
        manifestHydrated = true;
        const resolve = resolveManifestReady;
        if (resolve) { resolve(); resolveManifestReady = null; }
      }
    }, { once: true });
  } catch { /* ignore */ }
}

export function isManifestHydrated(): boolean {
  return manifestHydrated;
}

/* ── Prefer-prebuilt-only mode ──
 * When enabled, optimizedImg / toWebP refuse to emit Supabase Render API
 * URLs (`/storage/v1/render/image/public/...`). Instead they fall back to
 * the deterministic prebuilt manifest path (which is either present and
 * served from immutable storage, or 404s — the <img onError> handler can
 * trigger a per-unit rebuild). This locks public unit pages onto the
 * prebuilt pipeline so PageSpeed never sees on-the-fly transforms.
 *
 * InventoryDetail.tsx flips this on at mount, off at unmount.
 */
let PREFER_PREBUILT_ONLY = false;
export function setPreferPrebuiltOnly(v: boolean) { PREFER_PREBUILT_ONLY = v; }
export function isPreferPrebuiltOnly() { return PREFER_PREBUILT_ONLY; }

/** Wait for manifest hydration, with a timeout safeguard. */
export function whenManifestReady(timeoutMs = 1500): Promise<void> {
  if (manifestHydrated) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    manifestReadyPromise.then(finish);
    setTimeout(finish, Math.max(0, timeoutMs));
  });
}

/** Map an ImagePreset to the matching manifest variant key. */
function presetToVariantKey(preset: ImagePreset): keyof ManifestEntry {
  switch (preset) {
    case "productThumb":
    case "mini":
    case "cart":
      return "thumb";
    case "associated":
    case "category":
    case "manufacturer":
    case "catalog":
      return "card";
    case "detail":
      return "detail";
    case "hero":
      return "hero";
    case "heroMobile":
      return "detail";
    default:
      return "card";
  }
}

/** Normalize a src into the key format the manifest is built with.
 *  Bare /images/... paths must be prefixed with the splash-assets CDN base
 *  to match the keys produced by the build worker. */
function normalizeManifestKey(src: string): string {
  if (!src) return src;
  const noQuery = src.split("?")[0] ?? src;
  // Same-origin /images/splash-assets/ paths point at real storage objects
  // (splash-assets/<file>), so the manifest key must strip the
  // "/images/splash-assets" prefix — prefixing SB_OBJECT_BASE again would
  // hash a path that was never built and its variant always 404s.
  if (noQuery.startsWith("/images/splash-assets/")) {
    return SB_OBJECT_BASE + noQuery.slice("/images/splash-assets".length);
  }
  // Manifest keys were built using the Supabase object URL prefix, so
  // normalize bare /images/... paths against the Supabase base regardless
  // of where SB_ASSETS currently points (CDN vs Supabase).
  if (noQuery.startsWith("/images/")) return SB_OBJECT_BASE + noQuery;
  // If a URL comes in pointing at the CDN mirror, rewrite to the Supabase
  // object URL so it matches manifest keys.
  if (CDN_ASSETS && noQuery.startsWith(CDN_ASSETS)) {
    return SB_OBJECT_BASE + noQuery.slice(CDN_ASSETS.length);
  }
  // If a render-image URL slipped through, normalize back to the object path
  // so it matches the manifest keys (which are built from object URLs).
  if (noQuery.includes("/storage/v1/render/image/public/")) {
    return noQuery.replace(
      "/storage/v1/render/image/public/",
      "/storage/v1/object/public/",
    );
  }
  return noQuery;
}

/** Look up a pre-built variant. Returns null if not in manifest. */
function lookupVariant(src: string, preset: ImagePreset): string | null {
  if (!src) return null;
  const key = normalizeManifestKey(src);
  const entry = MANIFEST[key] ?? MANIFEST[src];
  if (!entry) {
    // Pre-hydration: trust the deterministic path; once the manifest is in
    // memory and the URL truly isn't there, return null and let the caller
    // fall back to the Render API.
    if (!manifestHydrated) {
      return deterministicManifestUrl(key, presetToVariantKey(preset));
    }
    return null;
  }
  const vk = presetToVariantKey(preset);
  return entry[vk] || entry.detail || null;
}

/**
 * Deterministic public URL for a pre-built variant: matches the path written
 * by the `build-site-image-manifest` edge function. Lets us serve immutable
 * 1y-cached WebPs on first paint, before the global manifest fetch resolves.
 */
export function deterministicManifestUrl(
  src: string,
  variant: keyof ManifestEntry = "detail",
): string | null {
  if (!src) return null;
  // Only meaningful for Supabase storage object URLs. The manifest builder
  // only processes /storage/v1/object/public/... URLs and writes variants to
  // the Supabase splash-assets bucket. CDN-hosted URLs (myrvpart.com) and
  // other origins have no prebuilt variants — return null so callers fall
  // back to the Render API or the direct URL.
  if (!/^https?:\/\//.test(src)) return null;
  if (!src.includes("/storage/v1/object/public/")) return null;
  const hash = sha256(src);
  const base = MANIFEST_USE_CDN ? MANIFEST_CDN_BASE : MANIFEST_SB_BASE;
  return `${base}/manifest/${hash}/${variant}.webp`;
}

/* ── Pre-optimised WebP assets in splash-assets bucket ── */
// CDN mirror of the splash-assets bucket. Synced nightly by the
// `sync-splash-to-cdn` edge function. Used for static assets, logos,
// hero images, bucket tiles, promo cards, and any /images/... references.
const CDN_ASSETS = "https://www.myrvpart.com/images/splash-assets";
// Original Supabase object URL base. Kept for manifest key normalization
// (manifest entries were built against Supabase URLs) and as a fallback
// reference. Most public-facing URLs now use CDN_ASSETS via SB_ASSETS.
const SB_OBJECT_BASE = "https://cpkvmigljcrjxctoixoq.supabase.co/storage/v1/object/public/splash-assets";
// Public delivery base.
// RE-FLIPPED (2026-06-13): Back to CDN_ASSETS (myrvpart.com mirror) to
// remove the third-party Supabase DNS+TLS handshake from the LCP path.
// LOCKSTEP REQUIREMENT: the <link rel=preload> hrefs in index.html and
// the og:image / twitter:image meta tags MUST also point at CDN_ASSETS
// so the preload byte-matches the rendered srcset. If you flip this
// constant, flip those too — otherwise preloads go unused and LCP
// regresses.
const SB_ASSETS = CDN_ASSETS;
// Same-origin copies of the LCP-critical splash assets (homepage hero
// variants, header logo, favicons). Hosted from /public/images/splash-assets/
// so they ship with zero third-party cookies, no extra DNS/TLS handshake,
// and reuse the same HTTP/2 connection as the HTML. Cached for 1 year
// via public/_headers. LOCKSTEP: index.html hero preloads must point at
// this same /images/splash-assets/ base.
const LOCAL_ASSETS = "/images/splash-assets";
// Bundled logos get content-hashed /assets/ URLs, which the host serves with
// a 1-year immutable cache. Legacy /images/splash-assets copies stay for
// saved settings and external references.
import logo400Url from "@/assets/brand/logo-400.webp?url";
import logo200Url from "@/assets/brand/logo-200.webp?url";
import logo240Url from "@/assets/brand/logo-240.webp?url";
/**
 * Base URL for prebuilt manifest variants. These are written to the Supabase
 * splash-assets bucket by the `build-site-image-manifest` edge function and
 * are NOT mirrored to the myrvpart.com CDN. Keep this pointing at Supabase
 * so deterministic prebuilt URLs (pre-hydration) actually resolve.
 */
const MANIFEST_BASE =
  "https://cpkvmigljcrjxctoixoq.supabase.co/storage/v1/object/public/splash-assets";
export const STATIC_ASSETS = {
  // Logos are served same-origin: requests to www.myrvpart.com carry that
  // domain's marketing cookies (fpestid, __ss_tk), which Lighthouse flags as
  // third-party cookies. LOCKSTEP: the header logo preload in
  // src/routes/__root.tsx must use this same /images/splash-assets/ base.
  logo400:    logo400Url,
  logo200:    logo200Url,
  logo240:    logo240Url,
  hero1920:   LOCAL_ASSETS + "/hero-1920.webp",
  hero1366:   LOCAL_ASSETS + "/hero-1366.webp",
  hero640:    LOCAL_ASSETS + "/hero-640.webp",
  hero480:    LOCAL_ASSETS + "/hero-480.webp",
  route66:    SB_ASSETS + "/route66-120.webp",
  // Served from the Supabase storage origin (not the myrvpart CDN) because
  // the CDN rejects any query string on splash-assets paths with a 404,
  // and external tools (PSI/cache-busters) sometimes append `?t=...`.
  // The Supabase origin tolerates query strings.
  route66_64: MANIFEST_BASE + "/route66-64.webp",
  // Hero images — desktop (1920) + mobile (640)
  heroAbout1920:        LOCAL_ASSETS + "/hero-about-1920.webp",
  heroAbout640:         LOCAL_ASSETS + "/hero-about-640.webp",
  heroSellRv1920:       LOCAL_ASSETS + "/hero-sell-rv-1920.webp",
  heroSellRv640:        LOCAL_ASSETS + "/hero-sell-rv-640.webp",
  heroForum1920:        LOCAL_ASSETS + "/hero-forum-1920.webp",
  heroForum640:         LOCAL_ASSETS + "/hero-forum-640.webp",
  heroBlog1920:         LOCAL_ASSETS + "/hero-blog-1920.webp",
  heroBlog640:          LOCAL_ASSETS + "/hero-blog-640.webp",
  heroRentals1920:      LOCAL_ASSETS + "/hero-rentals-1920.webp",
  heroRentals640:       LOCAL_ASSETS + "/hero-rentals-640.webp",
  heroTestimonials1920: LOCAL_ASSETS + "/hero-testimonials-1920.webp",
  heroTestimonials640:  LOCAL_ASSETS + "/hero-testimonials-640.webp",
  heroInventory1920:    LOCAL_ASSETS + "/hero-inventory-1920.webp",
  heroInventory640:     LOCAL_ASSETS + "/hero-inventory-640.webp",
  heroService1920:      LOCAL_ASSETS + "/hero-service-1920.webp",
  heroService640:       LOCAL_ASSETS + "/hero-service-640.webp",
  heroService1_1920:    LOCAL_ASSETS + "/hero-service-1-1920.webp",
  heroService1_640:     LOCAL_ASSETS + "/hero-service-1-640.webp",
  heroService2_1920:    LOCAL_ASSETS + "/hero-service-2-1920.webp",
  heroService2_640:     LOCAL_ASSETS + "/hero-service-2-640.webp",
  heroCamping1920:      LOCAL_ASSETS + "/hero-camping-1920.webp",
  heroCamping640:       LOCAL_ASSETS + "/hero-camping-640.webp",
  heroDoorPrizes1920:   SB_ASSETS + "/hero-door-prizes-1920.webp",
  heroDoorPrizes640:    SB_ASSETS + "/hero-door-prizes-640.webp",
  // Bucket tiles are bundled by Vite from src/assets/buckets so they ship
  // from the Vercel CDN (cold edge ~80ms) instead of Supabase storage
  // (cold edge 1.8-2.2s). This was the #1 LCP/PSI bottleneck on desktop.
  bucketParts400:       BUCKET_PARTS_400,
  bucketSales400:       BUCKET_SALES_400,
  bucketSales200:       BUCKET_SALES_400,
  bucketService400:     BUCKET_SERVICE_400,
  bucketService200:     BUCKET_SERVICE_400,
  bucketRentals400:     BUCKET_RENTALS_400,
  bucketRentals200:     BUCKET_RENTALS_400,
} as const;

/**
 * Return the cached promo image URL for a unit (compressed WebP in splash-assets/promo/).
 * Falls back to raw first image if stock_number not provided.
 */
export function promoImageUrl(stockNumber: string): string {
  return SB_ASSETS + "/promo/" + encodeURIComponent(stockNumber) + ".webp";
}

/** Absolute placeholder — served from Supabase storage origin because the
 *  myrvpart CDN copy is currently 404. */
export const PLACEHOLDER_URL = MANIFEST_BASE + "/placeholder.webp";

/** Returns a safe image src — falls back to the CDN placeholder when the
 *  input is empty/null/undefined. Use this anywhere a bare `<img src>` would
 *  otherwise default to "/placeholder.svg" (which 404s through the SPA
 *  catch-all on some CDN edges). */
export function safeImgSrc(src: string | null | undefined): string {
  if (!src || !String(src).trim()) return PLACEHOLDER_URL;
  return src;
}

/* ── Production detection ── */
const PROD_ORIGIN = "https://www.rexandsonsrvs.com";
const isProd =
  typeof window !== "undefined" &&
  (window.location.hostname === "www.rexandsonsrvs.com" ||
    window.location.hostname === "rexandsonsrvs.com");

/* ── Centralised image-size presets (px) ── */
export const IMAGE_SIZES = {
  detail:       { w: 800 },
  catalog:      { w: 400 },
  productThumb: { w: 100 },
  associated:   { w: 220 },
  category:     { w: 450 },
  manufacturer: { w: 420 },
  cart:         { w: 80 },
  mini:         { w: 70 },
  hero:         { w: 1920 },
  heroMobile:   { w: 600 },
} as const;

export type ImagePreset = keyof typeof IMAGE_SIZES;

/**
 * Rewrite /images/... paths to CDN-cached copies in splash-assets bucket.
 * Uses the Supabase Render API for on-the-fly resize + WebP conversion.
 */
function localToProxy(path: string, width: number, quality: number): string {
  // Same-origin splash assets (LOCAL_ASSETS) are already deliverable as-is;
  // don't prefix with SB_ASSETS and don't route through the Render API.
  if (path.startsWith(LOCAL_ASSETS + "/")) return path;
  const cdnUrl = SB_ASSETS + path;
  return toRenderUrl(cdnUrl, width, quality);
}

/**
 * Convert a local /images/... path to its CDN URL (no resize).
 * Use for direct <img src> where no transform is needed.
 */
export function cdnImg(path: string): string {
  if (path.startsWith(LOCAL_ASSETS + "/")) return path;
  if (path.startsWith("/images/")) return SB_ASSETS + path;
  return path;
}

/**
 * Render API URL for a splash-assets path (e.g. "/images/shop-by/btn-x.jpg").
 * Produces an on-the-fly WebP at the requested width/quality. Use for small
 * tile thumbnails where the raw splash-assets JPEG is dramatically oversized
 * for the rendered CSS box.
 */
export function splashRender(path: string, width: number, quality = 60): string {
  if (!path) return PLACEHOLDER_URL;
  const clean = path.startsWith("/images/") ? path.slice(1) : path; // -> "images/..."
  return `${MANIFEST_BASE.replace("/object/", "/render/image/")}/${clean}?width=${width}&quality=${quality}&resize=contain&format=webp`;
}

/**
 * Build the desktop hero LCP URL using the Supabase Render API directly,
 * bypassing the manifest. This MUST stay character-identical to the
 * preload URL written by the early <script> in index.html so the browser
 * can match the preload to the React-rendered <img>.
 */
export function buildHeroDesktopUrl(src: string): string {
  if (!src) return PLACEHOLDER_URL;
  if (src.includes('/storage/v1/object/public/')) {
    const base = src
      .replace('/storage/v1/object/public/', '/storage/v1/render/image/public/')
      .split('?')[0];
    return `${base}?width=1920&quality=60&resize=contain&format=webp`;
  }
  return src;
}

/**
 * Mobile counterpart of buildHeroDesktopUrl. MUST stay character-identical to
 * the mobile preload URL written by the early <script> in index.html.
 */
export function buildHeroMobileUrl(src: string): string {
  if (!src) return PLACEHOLDER_URL;
  if (src.includes('/storage/v1/object/public/')) {
    const base = src
      .replace('/storage/v1/object/public/', '/storage/v1/render/image/public/')
      .split('?')[0];
    return `${base}?width=600&quality=45&resize=contain&format=webp`;
  }
  return src;
}

/**
 * Direct Supabase Render API URL at an exact width/quality, bypassing the
 * manifest snap-to-variant logic. Use when the manifest's prebuilt sizes
 * (thumb/card/detail/hero) over-deliver for a small CSS box (e.g. floorplan,
 * logo). Non-Supabase URLs pass through unchanged.
 */
export function renderUrl(src: string, width: number, quality = 60): string {
  if (!src) return PLACEHOLDER_URL;
  let url = src;
  // Same-origin splash assets pass through — no Render API rewrite.
  if (url.startsWith(LOCAL_ASSETS + "/")) return url;
  if (url.startsWith('/images/')) url = SB_ASSETS + url;
  // Pre-built manifest variants (splash-assets/manifest/<hash>/{thumb,card,detail,hero}.webp)
  // are already correctly sized + WebP-encoded. Do NOT re-wrap them through the
  // Render API — that double-process can fall back to serving the source PNG.
  if (url.includes('/splash-assets/manifest/') && /\.webp(\?|$)/.test(url)) {
    return toCdnManifest(url.split('?')[0] ?? url);
  }
  // Other splash-assets WebPs may be oversized — fall through to the Render
  // API rewrite so width/quality are honored.
  // Rewrite object URL → render URL with width/quality.
  if (url.includes('/storage/v1/object/public/')) {
    const base = url
      .replace('/storage/v1/object/public/', '/storage/v1/render/image/public/')
      .split('?')[0];
    return `${base}?width=${width}&quality=${quality}&resize=contain&format=webp`;
  }
  return toRenderUrl(url, width, quality);
}

/**
 * Detect if a URL points to Supabase storage and rewrite to the Render API.
 * /storage/v1/object/public/... → /storage/v1/render/image/public/...?width=X&quality=80
 */
function toRenderUrl(src: string, width: number, quality = 60, extra?: { height?: number | undefined; resize?: string | undefined }): string {
  // Pre-built manifest variants are already correctly sized — skip Render API.
  // Other splash-assets WebPs may be oversized for the requested width, so let
  // them flow into the Render API rewrite below.
  if (src.includes('/splash-assets/manifest/') && /\.webp(\?|$)/.test(src)) {
    return toCdnManifest(src.split('?')[0] ?? src);
  }
  // Only transform Supabase storage object URLs
  if (src.includes('/storage/v1/object/public/')) {
    const renderSrc = src.replace(
      '/storage/v1/object/public/',
      '/storage/v1/render/image/public/'
    );
    // Strip any existing query params and add render params
    const base = renderSrc.split('?')[0];
    let url = `${base}?width=${width}&quality=${quality}`;
    if (extra?.height) url += `&height=${extra.height}`;
    url += `&resize=${extra?.resize ?? "contain"}`;
    url += `&format=webp`;
    return url;
  }
  return src;
}

/**
 * Return an optimised image URL using the Supabase Render API or wsrv.nl proxy.
 * For Supabase storage URLs: rewrites to /render/image/public/ with width + quality.
 * For local /images/ paths: proxies through wsrv.nl on production.
 * For other URLs: returns as-is.
 */
export function optimizedImg(
  src: string,
  preset: ImagePreset,
  opts?: { q?: number },
): string {
  if (!src) return PLACEHOLDER_URL;
  // Manifest-first: serve a pre-built immutable WebP if we have one.
  // Skip for heroMobile — the manifest's "detail" variant is 800px and
  // would over-deliver on mobile (~554px CSS box). Force Render API at 600px.
  if (preset !== "heroMobile") {
    const variant = lookupVariant(src, preset);
    if (variant) return variant;
  }
  const { w } = IMAGE_SIZES[preset];
  const q = opts?.q ?? 60;
  if (src.startsWith("/images/")) return localToProxy(src, w, q);
  if (PREFER_PREBUILT_ONLY) {
    const det = deterministicManifestUrl(normalizeManifestKey(src), presetToVariantKey(preset));
    return det ?? PLACEHOLDER_URL;
  }
  return toRenderUrl(src, w, q);
}

/**
 * Convert a storage URL or local path to an optimised WebP image.
 */
export function toWebP(src: string, opts?: { w?: number; h?: number; q?: number; resize?: string }): string {
  if (!src) return "";
  const width = opts?.w ?? 800;
  const quality = opts?.q ?? 60;
  const extra = (opts?.h || opts?.resize) ? { height: opts?.h, resize: opts?.resize } : undefined;
  // Manifest-first: serve a pre-built immutable WebP if we have one.
  // Pick the variant that best matches the requested width.
  const cleanSrc = normalizeManifestKey(src);
  const entry = MANIFEST[cleanSrc] ?? MANIFEST[src];
  if (entry) {
    let key: keyof ManifestEntry = "detail";
    if (width <= 120) key = "thumb";
    else if (width <= 500) key = "card";
    else if (width <= 1000) key = "detail";
    else key = "hero";
    const picked = entry[key] || entry.detail || entry.card || entry.hero || entry.thumb;
    if (picked) return picked;
  }
  // Manifest not yet hydrated — use the deterministic prebuilt path so the
  // LCP image doesn't have to wait on the 7MB+ /image-manifest fetch. Once
  // the manifest hydrates we'll know for sure if the variant exists; for
  // un-hydrated requests we trust that the build worker has produced the
  // standard variants for any image referenced in the DB.
  if (!manifestHydrated && cleanSrc.startsWith("http")) {
    let key: keyof ManifestEntry = "detail";
    if (width <= 120) key = "thumb";
    else if (width <= 500) key = "card";
    else if (width <= 1000) key = "detail";
    else key = "hero";
    const det = deterministicManifestUrl(cleanSrc, key);
    if (det) return det;
  }
  if (src.startsWith("/images/")) return localToProxy(src, width, quality);
  if (PREFER_PREBUILT_ONLY) {
    let key: keyof ManifestEntry = "detail";
    if (width <= 120) key = "thumb";
    else if (width <= 500) key = "card";
    else if (width <= 1000) key = "detail";
    else key = "hero";
    const det = deterministicManifestUrl(normalizeManifestKey(src), key);
    return det ?? PLACEHOLDER_URL;
  }
  return toRenderUrl(src, width, quality, extra);
}

/**
 * Modern-header logo responsive set. LOCKSTEP: the header <img> and the
 * root <link rel=preload imagesrcset> must use these exact strings so the
 * preload is reused (no double download). Rendered width: ~121px phones
 * (h-12), ~161px desktop (md:h-16).
 */
export const CARMAX_LOGO_SRCSET = `${STATIC_ASSETS.logo200} 200w, ${STATIC_ASSETS.logo240} 240w, ${STATIC_ASSETS.logo400} 400w`;
export const CARMAX_LOGO_SIZES = "(min-width: 768px) 161px, 121px";
